import { useEffect, useRef, useId, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';

/**
 * Canonical Prii modal. Heat-night style by default.
 *
 * Usage:
 *   <Modal isOpen={open} onClose={() => setOpen(false)} title="Edit profile">
 *     <ModalBody>...form fields...</ModalBody>
 *     <ModalFooter>
 *       <button>Cancel</button>
 *       <button>Save</button>
 *     </ModalFooter>
 *   </Modal>
 *
 * Props:
 *   isOpen        - boolean, controlled
 *   onClose       - () => void, called on backdrop click, escape, or close button
 *   title         - optional string; if provided, renders a header with close button
 *   size          - 'sm' | 'md' | 'lg' | 'xl'  (default 'md')
 *   variant       - 'centered' | 'sheet'  (default 'centered'; 'sheet' becomes bottom sheet on mobile)
 *   closeOnVeil   - boolean (default true) - clicking the backdrop closes
 *   closeOnEsc    - boolean (default true) - Escape key closes
 *   showClose     - boolean (default true) - show the X button in header
 *   safeAreaTop   - boolean (default false) - inset sheet panel below iOS safe area (mobile only)
 *   sheetAnchor   - 'bottom' | 'top' (default 'bottom') - sheet slides from bottom or top (mobile)
 *   keyboardAware - boolean (default false) - shrink panel to visual viewport when keyboard opens
 *   overlay       - 'viewport' | 'container' (default 'viewport'; container = absolute over parent)
 *   glassSurface  - boolean (default true) - frosted glass panel matching header/tab bar
 *   children      - ModalHeader/Body/Footer or arbitrary content
 */
export default function Modal({
  isOpen,
  onClose,
  title,
  size = 'md',
  variant = 'centered',
  closeOnVeil = true,
  closeOnEsc = true,
  showClose = true,
  safeAreaTop = false,
  sheetAnchor = 'bottom',
  keyboardAware = false,
  overlay = 'viewport',
  glassSurface = true,
  children,
}) {
  const isContained = overlay === 'container';
  const titleId = useId();
  const panelRef = useRef(null);
  const previousActiveRef = useRef(null);
  const [keyboardMaxHeight, setKeyboardMaxHeight] = useState(null);

  // Lock body scroll while open (viewport overlays only — container overlays use parent overflow)
  useEffect(() => {
    if (!isOpen || isContained) return;
    const scrollY = window.scrollY;
    const original = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      width: document.body.style.width,
    };
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    return () => {
      document.body.style.overflow = original.overflow;
      document.body.style.position = original.position;
      document.body.style.top = original.top;
      document.body.style.width = original.width;
      window.scrollTo(0, scrollY);
    };
  }, [isOpen, isContained]);

  // Shrink panel to visible viewport when the on-screen keyboard is open (mobile search sheets).
  useEffect(() => {
    if (!isOpen || !keyboardAware) {
      setKeyboardMaxHeight(null);
      return undefined;
    }
    const vv = window.visualViewport;
    if (!vv) return undefined;

    const update = () => {
      const root = getComputedStyle(document.documentElement);
      const safeTop = parseFloat(root.getPropertyValue('--safe-area-top')) || 0;
      setKeyboardMaxHeight(Math.max(vv.height - safeTop, 180));
    };

    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      setKeyboardMaxHeight(null);
    };
  }, [isOpen, keyboardAware]);

  // Every other viewport modal sits in the part of the screen the keyboard leaves (Sam, 2026-10-26: the keyboard hid
  // "Create group" under "Name your group", so naming a group took two taps). The backdrop follows the visual viewport,
  // so a centred panel centres above the keyboard and its footer button stays tappable.
  const [visibleBox, setVisibleBox] = useState(null);
  useEffect(() => {
    if (!isOpen || isContained || keyboardAware) return undefined;
    const vv = window.visualViewport;
    if (!vv) return undefined;
    const update = () => {
      const covered = window.innerHeight - vv.height;
      setVisibleBox(covered > 80 ? { top: vv.offsetTop, height: vv.height } : null);
    };
    vv.addEventListener('resize', update);
    vv.addEventListener('scroll', update);
    update();
    return () => {
      vv.removeEventListener('resize', update);
      vv.removeEventListener('scroll', update);
      setVisibleBox(null);
    };
  }, [isOpen, isContained, keyboardAware]);

  // Escape key
  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, closeOnEsc, onClose]);

  // Focus management - move focus into panel on open, restore on close
  useEffect(() => {
    if (!isOpen) return;
    previousActiveRef.current = document.activeElement;
    const id = setTimeout(() => {
      panelRef.current?.focus();
    }, 0);
    return () => {
      clearTimeout(id);
      previousActiveRef.current?.focus?.();
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const sizeClass =
    {
      sm: 'max-w-sm',
      md: 'max-w-md',
      lg: 'max-w-lg',
      xl: 'max-w-2xl',
    }[size] ?? 'max-w-md';

  const isTopSheet = variant === 'sheet' && sheetAnchor === 'top';

  const positionClass =
    variant === 'sheet'
      ? isTopSheet
        ? 'items-start sm:items-center'
        : 'items-end sm:items-center'
      : safeAreaTop
        ? 'items-start sm:items-center'
        : 'items-center';

  // All modal panels use full corner radius (including mobile sheets).
  const radiusClass = 'rounded-modal';

  const handleVeilClick = (e) => {
    if (!closeOnVeil) return;
    if (e.target === e.currentTarget) onClose?.();
  };

  const backdropPositionClass = isContained ? 'absolute' : 'fixed';

  const backdropPaddingClass = isContained
    ? 'p-0'
    : isTopSheet
      ? 'px-0 pt-[var(--safe-area-top)] pb-0 sm:p-6'
      : safeAreaTop
        ? 'px-4 pt-[var(--safe-area-top)] pb-6 sm:p-6'
        : 'px-4 py-6 sm:p-6';

  const panelMaxHeightClass = isContained
    ? 'h-full max-h-full min-h-0'
    : keyboardAware
      ? ''
      : isTopSheet
        ? 'max-h-[calc(100dvh-var(--safe-area-top))] sm:max-h-[calc(100dvh-3rem)]'
        : safeAreaTop
          ? 'max-h-[calc(100dvh-var(--safe-area-top)-1.5rem)] sm:max-h-[calc(100dvh-3rem)]'
          : 'max-h-[calc(100dvh-3rem)] sm:max-h-[calc(100dvh-3rem)]';

  const panelSurfaceClass = glassSurface ? 'modal-glass-surface' : 'bg-modal-bg';

  const panelBorderClass = glassSurface
    ? isTopSheet
      ? 'border-x-0 border-b border-t-0 border-[var(--glass-frosted-border)] sm:border sm:border-[var(--glass-frosted-border)]'
      : 'border border-[var(--glass-frosted-border)]'
    : isTopSheet
      ? 'border-x-0 border-b border-t-0 border-modal-border sm:border sm:border-modal-border'
      : 'border border-modal-border';

  const panelStyle =
    keyboardAware && keyboardMaxHeight != null
      ? { maxHeight: keyboardMaxHeight }
      : visibleBox
        ? { maxHeight: visibleBox.height - 24 }
        : undefined;

  const veilClass = glassSurface
    ? 'bg-modal-veil-glass backdrop-blur-modal'
    : 'bg-modal-veil backdrop-blur-modal';

  const modalTree = (
    <div
      className={`${backdropPositionClass} inset-0 z-modal-backdrop flex justify-center ${positionClass} ${backdropPaddingClass} ${veilClass}`}
      onClick={handleVeilClick}
      aria-hidden={!isOpen}
      style={
        visibleBox ? { top: visibleBox.top, height: visibleBox.height, bottom: 'auto' } : undefined
      }
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        style={panelStyle}
        className={`relative z-modal w-full ${sizeClass} ${radiusClass} ${panelSurfaceClass} shadow-2xl outline-none flex flex-col overflow-hidden ${panelMaxHeightClass} ${panelBorderClass}`}
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <ModalHeader title={title} id={titleId} onClose={showClose ? onClose : undefined} />
        )}
        {children}
      </div>
    </div>
  );

  if (isContained) return modalTree;
  return createPortal(modalTree, document.body);
}

/**
 * Modal header with title and optional close button.
 * Use directly only if you need a header without the auto-rendering from <Modal title="...">.
 */
export function ModalHeader({ title, id, onClose, children }) {
  return (
    <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4">
      <div className="flex-1 min-w-0">
        {title && (
          <h2 id={id} className="text-lg font-semibold text-ink leading-snug">
            {title}
          </h2>
        )}
        {children}
      </div>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="shrink-0 -mr-2 -mt-2 p-2 rounded-full text-ink/70 hover:text-ink hover:bg-white/10 transition-colors"
        >
          <X size={20} />
        </button>
      )}
    </div>
  );
}

/**
 * Scrollable content area. Auto-flexes to fill available space.
 */
export function ModalBody({ children, className = '' }) {
  return (
    <div className={`flex-1 overflow-y-auto px-6 py-4 text-ink/90 ${className}`}>{children}</div>
  );
}

/**
 * Action button row. Default is right-aligned (Cancel, Confirm).
 * align="split" makes children share width equally (Cancel | Confirm at 50/50).
 * align="stack" stacks children vertically (used by Going options modals).
 */
export function ModalFooter({ children, align = 'right', className = '' }) {
  const layoutClass =
    {
      right: 'flex justify-end gap-3',
      split: 'grid grid-cols-2 gap-3',
      stack: 'flex flex-col gap-3',
    }[align] ?? 'flex justify-end gap-3';

  return <div className={`px-6 pt-2 pb-6 ${layoutClass} ${className}`}>{children}</div>;
}
